import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface WorkoutSet {
  id: string;
  session_id: string;
  exercise_id: string;
  set_number: number;
  weight?: number;
  reps?: string;
  completed: boolean;
  notes?: string;
  prev_weight?: number;
  prev_reps?: number;
  created_at: string;
}

interface WorkoutSession {
  id: string;
  user_id: string;
  program_id?: string;
  scheduled_date?: string;
  status: string;
  started_at?: string;
  completed_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface SyncQueueItem {
  id: string;
  table: string;
  operation: 'insert' | 'update' | 'delete';
  data: any;
  timestamp: number;
  synced: boolean;
  error?: string;
}

interface FitnessDB extends DBSchema {
  workout_sessions: {
    key: string;
    value: WorkoutSession;
    indexes: { 'by-user': string; 'by-date': string };
  };
  workout_sets: {
    key: string;
    value: WorkoutSet;
    indexes: { 'by-session': string };
  };
  sync_queue: {
    key: string;
    value: SyncQueueItem;
    indexes: { 'by-synced': number };
  };
}

const DB_NAME = 'fitness-offline-db';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<FitnessDB> | null = null;

export async function getDB(): Promise<IDBPDatabase<FitnessDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<FitnessDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Workout Sessions store
      if (!db.objectStoreNames.contains('workout_sessions')) {
        const sessionStore = db.createObjectStore('workout_sessions', { keyPath: 'id' });
        sessionStore.createIndex('by-user', 'user_id');
        sessionStore.createIndex('by-date', 'scheduled_date');
      }

      // Workout Sets store
      if (!db.objectStoreNames.contains('workout_sets')) {
        const setsStore = db.createObjectStore('workout_sets', { keyPath: 'id' });
        setsStore.createIndex('by-session', 'session_id');
      }

      // Sync Queue store
      if (!db.objectStoreNames.contains('sync_queue')) {
        const queueStore = db.createObjectStore('sync_queue', { keyPath: 'id' });
        queueStore.createIndex('by-synced', 'synced');
      }
    },
  });

  return dbInstance;
}

// Workout Sessions operations
export async function saveSessionLocally(session: WorkoutSession) {
  const db = await getDB();
  await db.put('workout_sessions', session);
}

export async function getSessionsLocally(userId: string): Promise<WorkoutSession[]> {
  const db = await getDB();
  const allSessions = await db.getAllFromIndex('workout_sessions', 'by-user', userId);
  return allSessions.sort((a, b) => 
    new Date(b.scheduled_date || b.created_at).getTime() - 
    new Date(a.scheduled_date || a.created_at).getTime()
  ).slice(0, 10);
}

// Workout Sets operations
export async function saveSetsLocally(sets: WorkoutSet[]) {
  const db = await getDB();
  const tx = db.transaction('workout_sets', 'readwrite');
  await Promise.all(sets.map(set => tx.store.put(set)));
  await tx.done;
}

export async function getSetsForSessionLocally(sessionId: string): Promise<WorkoutSet[]> {
  const db = await getDB();
  return db.getAllFromIndex('workout_sets', 'by-session', sessionId);
}

// Sync Queue operations
export async function addToSyncQueue(
  table: string,
  operation: 'insert' | 'update' | 'delete',
  data: any
) {
  const db = await getDB();
  const item: SyncQueueItem = {
    id: crypto.randomUUID(),
    table,
    operation,
    data,
    timestamp: Date.now(),
    synced: false,
  };
  await db.add('sync_queue', item);
}

export async function getPendingSyncItems(): Promise<SyncQueueItem[]> {
  const db = await getDB();
  return db.getAllFromIndex('sync_queue', 'by-synced', 0);
}

export async function markSyncItemComplete(id: string) {
  const db = await getDB();
  const item = await db.get('sync_queue', id);
  if (item) {
    item.synced = true;
    await db.put('sync_queue', item);
  }
}

export async function markSyncItemError(id: string, error: string) {
  const db = await getDB();
  const item = await db.get('sync_queue', id);
  if (item) {
    item.error = error;
    await db.put('sync_queue', item);
  }
}

export async function clearSyncedItems() {
  const db = await getDB();
  const items = await db.getAllFromIndex('sync_queue', 'by-synced', 1);
  const tx = db.transaction('sync_queue', 'readwrite');
  await Promise.all(items.map(item => tx.store.delete(item.id)));
  await tx.done;
}
